import { ChangeDetectionStrategy, Component, inject, OnInit } from '@angular/core';
import { getAllAbsenteeOptions } from '@app/reducers/orm/absentee-option/absentee-option.service';
import { Store } from '@ngrx/store';
import { AppState } from '@reducers/interfaces';
import { hasPermission } from '@sb/shared/permission/util';
import { parseDate, toISODateString, weekYearEnd, weekYearStart } from '@shiftbase-com/ts-shared/utilities';
import { clsx } from 'clsx';
import { getISOWeekYear } from 'date-fns';
import { BehaviorSubject, Observable, combineLatest as observableCombineLatest } from 'rxjs';
import { map, repeatWhen, startWith, switchMap, tap, withLatestFrom } from 'rxjs/operators';

import { selectAuthUserId } from '../../../reducers/auth/auth.selectors';
import { getPermissionState } from '../../../reducers/auth/permission.helper';
import { LoadCorrectionRequest } from '../../../reducers/orm/correction/correction.api';
import type { CorrectionModel } from '../../../reducers/orm/correction/correction.model';
import { CorrectionService, getUserCorrections } from '../../../reducers/orm/correction/correction.service';
import { EmployeeModel } from '../../../reducers/orm/employee/employee.model';
import { getAuthenticatedUser, getEmployeeTeamDepartments } from '../../../reducers/orm/employee/employee.selectors';
import { groupDayStatsPerWeek } from '../../+employees/shared/employee-plusmin-overview/group-day-stats-per-week.helper';

// The `plus_min` stats endpoint is untyped end to end (`CorrectionApi.loadPlusMinStats` returns
// `Observable<any>`). Only `date` is read here; the rows and totals are handed straight to
// `employee-plusmin-overview`, which is where their remaining fields are rendered.
interface PlusMinDayStat {
  date: string;
}

interface PlusMinWeekStats {
  week_stats: unknown;
  totals: unknown;
}

interface PlusMinDayStats {
  day_stats: PlusMinDayStat[];
  totals: unknown;
}

function mapStats(
  weekStats: PlusMinWeekStats | null,
  dayStats: PlusMinDayStats | null,
  corrections: CorrectionModel[],
) {
  return {
    corrections: corrections,
    week_stats: weekStats ? weekStats.week_stats : weekStats,
    day_stats: groupDayStatsPerWeek(dayStats),
    totals: getTotals(weekStats, dayStats),
  };
}

function getTotals(weekStats: PlusMinWeekStats | null, dayStats: PlusMinDayStats | null) {
  if (!weekStats && !dayStats) {
    return weekStats;
  }

  if (weekStats) {
    return weekStats.totals;
  }

  return dayStats?.totals;
}

type PlusMinOverviewStats = ReturnType<typeof mapStats>;

@Component({
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
  selector: 'my-plus-minus',
  template: `
    <employee-plusmin-overview
      [title]="'My plus minus hours' | translate"
      [start]="(user | async)?.start"
      [userId]="(user | async)?.id"
      [year]="year$ | async"
      [stats]="stats | async"
      [loadingCorrections]="loadingCorrections"
      [loadingStats]="loadingStats"
      [absenteeOptions]="absenteeOptions | async"
      [showAbsence]="showAbsence$ | async"
      (yearChange)="onYearChange($event)"
      (removeCorrection)="removeCorrection($event)"
    ></employee-plusmin-overview>
  `,
  // eslint-disable-next-line @typescript-eslint/naming-convention
  host: { '[class]': 'hostClasses' },
})
export class MyPlusMinusComponent implements OnInit {
  private store = inject<Store<AppState>>(Store);
  private correctionService = inject(CorrectionService);

  hostClasses = clsx('flex flex-col px-5');

  stats: Observable<PlusMinOverviewStats>;
  user: Observable<any> = this.getUser();
  // The year select emits its option values as strings, so the subject carries both.
  year$ = new BehaviorSubject<number | string>(getISOWeekYear(new Date()));
  absenteeOptions = this.store.select(getAllAbsenteeOptions);
  loadingCorrections = true;
  loadingStats = true;

  showAbsence$ = this.showAbsence();

  ngOnInit() {
    this.stats = observableCombineLatest(this.user, this.year$).pipe(
      switchMap(([user, year]) => this.getPlusMinStats(user.id, year)),
    );
  }

  private getUser() {
    return this.store.select(getAuthenticatedUser).pipe(
      map((employee: EmployeeModel) => ({
        id: employee.id,
        start: parseDate(employee.startdate).getFullYear().toString(),
      })),
    );
  }

  private showAbsence() {
    return this.store.select(selectAuthUserId).pipe(
      withLatestFrom(this.store.select(getPermissionState), this.store.select(getEmployeeTeamDepartments)),
      map(([employeeId, permissionState, employeeTeamDepartments]) =>
        hasPermission(
          {
            userId: employeeId,
            permissions: ['View absentee', 'View own absentee'],
            departments: employeeTeamDepartments[employeeId],
          },
          permissionState,
        ),
      ),
    );
  }

  private getPlusMinStats(userId: string, year: number | string) {
    this.loadingStats = true;

    const yearStart = toISODateString(weekYearStart(year));
    const yearEnd = toISODateString(weekYearEnd(year));

    //load corrections
    const correction$ = this.getCorrections(userId, yearStart, yearEnd);

    return observableCombineLatest(
      this.correctionService.loadEmployeePlusMinStats(userId, yearStart, yearEnd, 'week').pipe(
        startWith(null),
        tap((data) => {
          if (data) {
            this.loadingStats = false;
          }
        }),
        repeatWhen(() => this.correctionService.listenToChanges()),
      ),
      this.correctionService.loadEmployeePlusMinStats(userId, yearStart, yearEnd, 'day').pipe(
        startWith(null),
        repeatWhen(() => this.correctionService.listenToChanges()),
      ),
      correction$,
      mapStats,
    );
  }

  private getCorrections(userId: string, yearStart: string, yearEnd: string) {
    const correctionRequest: LoadCorrectionRequest = {
      user_id: userId,
      min_date: yearStart,
      max_date: yearEnd,
      type: 'Overtime',
    };

    this.correctionService.load(correctionRequest).subscribe(() => (this.loadingCorrections = false));

    return this.store.select(getUserCorrections(userId, yearStart, yearEnd, correctionRequest.type));
  }

  onYearChange(year: string) {
    this.year$.next(year);
  }

  removeCorrection(correctionId: string) {
    this.correctionService.remove(correctionId).subscribe();
  }
}
